import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { QueryClientConfig } from '@tanstack/react-query';
import * as React from 'react';

/**
 * Centralised QueryClient defaults for CGuard SPAs. Each app composes its
 * own root provider; this helper keeps stale-time + retry policy uniform
 * across operator-console, ssp-portal, admin-console, commissioning-pwa.
 *
 * Operator-console overrides individual queries via the per-hook options
 * orval exposes — those overrides take precedence over the defaults below.
 */
export const cguardDefaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
};

export function createCguardQueryClient(overrides?: QueryClientConfig): QueryClient {
  return new QueryClient({
    ...cguardDefaultQueryClientConfig,
    ...overrides,
    defaultOptions: {
      ...cguardDefaultQueryClientConfig.defaultOptions,
      ...overrides?.defaultOptions,
      queries: {
        ...cguardDefaultQueryClientConfig.defaultOptions?.queries,
        ...overrides?.defaultOptions?.queries,
      },
      mutations: {
        ...cguardDefaultQueryClientConfig.defaultOptions?.mutations,
        ...overrides?.defaultOptions?.mutations,
      },
    },
  });
}

export interface CguardQueryProviderProps {
  client?: QueryClient;
  children: React.ReactNode;
}

export function CguardQueryProvider({
  client,
  children,
}: CguardQueryProviderProps): React.JSX.Element {
  const fallback = React.useMemo(() => client ?? createCguardQueryClient(), [client]);
  return <QueryClientProvider client={fallback}>{children}</QueryClientProvider>;
}
